import { createContext, useContext, useState, type ReactNode } from "react";

// Types
export interface UploadedFiles {
  storyboard: File | null;
  manuscript: File | null;
  figmaFile: File | null;
  brief: File | null;
}

export interface UploadResponse {
  DocID: string;
  filename: string;
}

export interface UploadResponses {
  storyboard: UploadResponse | null;
  manuscript: UploadResponse | null;
  figmaFile: UploadResponse | null;
  brief: UploadResponse | null;
}

// Preview data types
export interface StoryboardFrame {
  frame_id: string;
  doc_id: string;
  page: number;
  dimension: string | null;
}

export interface ManuscriptContent {
  type: string;
  level: string | null;
  text: string | null;
  data: any;
}

export interface BannerOption {
  heading: string;
  details: string;
}

export interface ManuscriptPreviewData {
  full_content: ManuscriptContent[];
  banner_options: {
    [key: string]: BannerOption;
  };
}

export interface BriefSize {
  Sheet: string;
  Field_Name: string;
  Sizes: string[];
}

export interface BriefPreviewData {
  sizes: BriefSize[];
}

export interface PreviewData {
  storyboard: StoryboardFrame[] | null;
  manuscript: ManuscriptPreviewData | null;
  brief: BriefPreviewData | null;
}

interface FigmaDetails {
  url: string;
  frameName: string;
  designNotes: string;
}

interface MultipleFileUploadContextType {
  uploadedFiles: UploadedFiles;
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFiles>>;
  uploadResponses: UploadResponses;
  setUploadResponses: React.Dispatch<React.SetStateAction<UploadResponses>>;
  previewData: PreviewData;
  setPreviewData: React.Dispatch<React.SetStateAction<PreviewData>>;
  figmaDetails: FigmaDetails;
  setFigmaDetails: React.Dispatch<React.SetStateAction<FigmaDetails>>;
  activePreview: string | null;
  setActivePreview: React.Dispatch<React.SetStateAction<string | null>>;
  resetAllFiles: () => void;
}

const MultipleFileUploadContext = createContext<MultipleFileUploadContextType | undefined>(
  undefined
);

interface MultipleFileUploadProviderProps {
  children: ReactNode;
}

export const MultipleFileUploadProvider: React.FC<MultipleFileUploadProviderProps> = ({
  children,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>({
    storyboard: null,
    manuscript: null,
    figmaFile: null,
    brief: null,
  });

  const [uploadResponses, setUploadResponses] = useState<UploadResponses>({
    storyboard: null,
    manuscript: null,
    figmaFile: null,
    brief: null,
  });

  const [previewData, setPreviewData] = useState<PreviewData>({
    storyboard: null,
    manuscript: null,
    brief: null,
  });

  const [figmaDetails, setFigmaDetails] = useState<FigmaDetails>({
    url: "",
    frameName: "",
    designNotes: "",
  });

  const [activePreview, setActivePreview] = useState<string | null>(null);

  const resetAllFiles = () => {
    setUploadedFiles({
      storyboard: null,
      manuscript: null,
      figmaFile: null,
      brief: null,
    });
    setUploadResponses({
      storyboard: null,
      manuscript: null,
      figmaFile: null,
      brief: null,
    });
    setPreviewData({
      storyboard: null,
      manuscript: null,
      brief: null,
    });
    setFigmaDetails({
      url: "",
      frameName: "",
      designNotes: "",
    });
    setActivePreview(null);
  };

  return (
    <MultipleFileUploadContext.Provider
      value={{
        uploadedFiles,
        setUploadedFiles,
        uploadResponses,
        setUploadResponses,
        previewData,
        setPreviewData,
        figmaDetails,
        setFigmaDetails,
        activePreview,
        setActivePreview,
        resetAllFiles,
      }}
    >
      {children}
    </MultipleFileUploadContext.Provider>
  );
};

export const useMultipleFileUpload = (): MultipleFileUploadContextType => {
  const context = useContext(MultipleFileUploadContext);
  if (!context) {
    throw new Error("useMultipleFileUpload must be used within MultipleFileUploadProvider");
  }
  return context;
};
